import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { DataPacket_Kind, RemoteParticipant, RoomEvent } from "livekit-client";
import { useCallback, useEffect } from "react";

type MicLockHandler = (locked: boolean, fromIdentity?: string) => void;

export function useMicLockListener(onMicLock: MicLockHandler) {
  const { livekitContext } = useLivekitContext();
  const { room } = livekitContext ?? {};

  useEffect(() => {
    if (!room) return;

    const handler = (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      kind?: DataPacket_Kind,
      topic?: string | undefined
    ) => {
      try {
        if (topic && topic !== "mic-lock") return;
        const json = new TextDecoder().decode(payload);
        const data = JSON.parse(json);

        if (data?.type !== "mic-lock") return;
        const me = room.localParticipant?.identity;
        if (data.target && data.target !== me && data.target !== "*") return;

        onMicLock(Boolean(data.locked), participant?.identity);
      } catch (e) {
        console.error("Failed to parse data packet", e);
      }
    };

    room.on(RoomEvent.DataReceived, handler);
    return () => {
      room.off(RoomEvent.DataReceived, handler);
    };
  }, [room, onMicLock]);
}

export function MicLockReceiverExample({
  onUIChange,
}: {
  onUIChange?: (state: "locked" | "unlocked", by?: string) => void;
}) {
  const { livekitContext } = useLivekitContext();
  const { room } = livekitContext ?? {};
  useMicLockListener(
    useCallback(
      async (locked, from) => {
        if (!room) return;
        onUIChange?.(locked ? "locked" : "unlocked", from);
        await room.localParticipant.setMicrophoneEnabled(!locked);
      },
      [room, onUIChange]
    )
  );

  return null;
}
